import React, { Fragment, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { connect } from "react-redux";
import { getModelId, getDeviceByModelNumber } from "../../action/addvcModel";
import { setAlert } from "../../action/alert";
import Alert from "../layout/Alert";
import ResponsiveContentWrapper from "../layout/ResponsiveContentWrapper";
import { Link } from "react-router-dom";
import Spinner from "../layout/Spinner";
import Moment from "react-moment";
import customConfig from "../../config";
import EditIcon from "@mui/icons-material/Edit";
import DownloadIcon from "@mui/icons-material/ArrowDownward";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import {
  SearchSectionLabel,
  SectionHeading,
} from "../Common/Typography/AppTypography";
import Grid2 from "@mui/material/Grid2";
import { getFreqToPower } from "../../action/report";
import FormInput from "../Common/FormInputs/MuiTextField";
import AppButton from "../Common/Button/Button";
import InfoField from "../Common/InfoField/InfoField";
import SearchIcon from "@mui/icons-material/Search";
import { useTheme } from "@mui/material/styles";
import { useDesignTokens } from "../../theme/useDesignTokens";
import DataTable from "../Common/DataTable/DataTable";
import "../Common/searchFilterLayout.css";
import "./DeviceDetails.css";
import { selectMenuProps } from "../../utils/selectMenuProps";

const SearchModel = ({
  getModelId,
  getDeviceByModelNumber,
  ModelData: { ModelData, loading },
  match,
  roles,
  wrapperClass,
  getFreqToPower,
  freqToPowerData,
}) => {
  const theme = useTheme();
  const { colors } = useDesignTokens();
  const primaryColor = colors?.primary?.main || theme.palette.primary.main;
  const focusOutlineSx = {
    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: `${primaryColor} !important`,
      borderWidth: "1px",
    },
    "& .MuiInputLabel-root.Mui-focused": {
      color: `${primaryColor} !important`,
    },
  };
  const fieldHeight = 45;
  const { modelNum } = useParams();
  const [text, setText] = useState("");
  const [showText, setShowText] = useState(false);
  const [searchBy, setSearchBy] = useState(
    modelNum ? "modelNumber" : "modelId",
  );

  useEffect(() => {
    if (modelNum) {
      ModelData = {};
      getDeviceByModelNumber(modelNum);
      setShowText(true);
      setText("");
    }
  }, []);

  const onSubmit = (e) => {
    ModelData = {};
    setShowText(true);
    e.preventDefault();
    if (searchBy === "modelId") {
      getModelId(text);
    } else {
      getDeviceByModelNumber(text);
    }
    getFreqToPower({ searchBy, text });
    setText("");
  };

  const onChange = (e) => setText(e.target.value);

  const changeSearchBy = (e) => {
    setSearchBy(e.target.value);
    ModelData = {};
    setShowText(false);
  };

  const downloadDeviceMetadata = () => {
    const blob = new Blob([JSON.stringify(ModelData)], { type: "text/json" });
    let url = window.URL.createObjectURL(blob);
    let a = document.createElement("a");
    a.href = url;
    a.download = `Device Model Data- ${ModelData.modelNumber}.json`;
    a.click();
  };

  const formatAttributeValues = (vals) => {
    if (vals == null) return "N/A";
    if (Array.isArray(vals)) {
      return vals
        .map((elem) => {
          if (typeof elem === "string" || typeof elem === "number")
            return String(elem);
          if (Array.isArray(elem)) return elem.join(", ");
          const keys = Object.keys(elem);
          return `${keys[0]} : ${elem[keys[0]]} (${elem[keys[1]]})`;
        })
        .join(" / ");
    }
    return String(vals);
  };

  const formatRange = (range) => {
    if (!range || typeof range !== "object") return "N/A";
    const text = Object.keys(range)
      .map((rangeKey) => {
        const arr = range[rangeKey];
        if (!Array.isArray(arr)) return "";
        const vals = arr.map((r) => `${r.value ?? ""} ${r.unit || ""}`.trim()).filter(Boolean).join(" / ");
        return vals ? `${rangeKey}: ${vals}` : "";
      })
      .filter(Boolean)
      .join("; ");
    return text || "N/A";
  };

  const formatCapabilityValue = (attribute) => {
    if (!attribute) return "N/A";
    if (attribute.values != null) {
      const formatted = formatAttributeValues(attribute.values);
      return formatted || "N/A";
    }
    if (attribute.intValues != null) {
      const formatted = formatAttributeValues(attribute.intValues);
      return formatted || "N/A";
    }
    if (attribute.range) return formatRange(attribute.range);
    return "N/A";
  };

  const buildCapabilityRows = (capabilities, rowPrefix) =>
    (capabilities || []).flatMap((item, itemIndex) => {
      const attributes = Array.isArray(item?.attributes) ? item.attributes : [];
      const capabilityName = item?.capability || "N/A";

      if (attributes.length === 0) {
        return [
          {
            rowId: `${capabilityName}-${itemIndex}-na`,
            capability: capabilityName,
            attributeKey: "N/A",
            values: "N/A",
            minFwVersion: "N/A",
          },
        ];
      }

      return attributes.map((attribute, attributeIndex) => ({
        rowId: `${rowPrefix}-${capabilityName}-${itemIndex}-${attribute?.key || attributeIndex}`,
        capability: attributeIndex === 0 ? capabilityName : "",
        attributeKey: attribute?.key || "N/A",
        values: formatCapabilityValue(attribute),
        minFwVersion:
          attribute?.minFwVersion != null && attribute.minFwVersion !== ""
            ? String(attribute.minFwVersion)
            : "N/A",
      }));
    });

  const controlCapabilityRows = buildCapabilityRows(
    ModelData.controlCapabilities,
    "control",
  );

  const controlCapabilityColumns = [
    {
      key: "capability",
      label: "Capability",
      minWidth: 180,
      headerLabelSx: { color: primaryColor },
    },
    {
      key: "attributeKey",
      label: "Attribute Key",
      minWidth: 180,
      headerLabelSx: { color: primaryColor },
    },
    {
      key: "values",
      label: "Values",
      minWidth: 520,
      headerLabelSx: { color: primaryColor },
    },
  ];

  const statusCapabilityRows = buildCapabilityRows(
    ModelData.statusCapabilitiesV2,
    "status",
  );

  const statusCapabilityColumns = controlCapabilityColumns;

  const diagCapabilityRows = buildCapabilityRows(
    ModelData.diagCapabilities,
    "diag",
  );

  const configCapabilityRows = buildCapabilityRows(
    ModelData.configCapabilities,
    "config",
  );

  const configCapabilityColumns = [
    {
      key: "capability",
      label: "Capability",
      minWidth: 220,
      headerLabelSx: { color: primaryColor },
    },
    {
      key: "attributeKey",
      label: "Attribute Key",
      minWidth: 180,
      headerLabelSx: { color: primaryColor },
    },
    {
      key: "values",
      label: "Values",
      minWidth: 520,
      headerLabelSx: { color: primaryColor },
    },
    {
      key: "minFwVersion",
      label: "Min FW Version",
      minWidth: 160,
      headerLabelSx: { color: primaryColor },
    },
  ];

  if (loading) {
    return <Spinner />;
  }
  return (
    <ResponsiveContentWrapper>
      <Box
        sx={{
          width: "100%",
          maxWidth: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          px: { xs: 1, sm: 2 },
        }}
      >
        {roles.includes(customConfig.roleProductManager) && (
          <Box sx={{ mb: 2 }}>
            <AppButton
              component={Link}
              to="/addNewDeviceModel"
              variant="primary"
              size="medium"
              sx={{ minWidth: "auto" }}
            >
              Add New Device Model
            </AppButton>
          </Box>
        )}

        <Grid2 container spacing={2}>
          <Grid2 size={12}>
            <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", lg: "auto 1fr" },
                  rowGap: 2,
                  columnGap: { xs: 2, lg: "116px" },
                  alignItems: "center",
                }}
              >
                <SectionHeading sx={{ mb: 0 }}>
                  Search Device Model
                </SectionHeading>
                <Box
                  component="form"
                  onSubmit={onSubmit}
                  sx={{
                    display: "grid",
                    gap: 1.5,
                    gridTemplateColumns: {
                      xs: "1fr",
                      sm: "1fr",
                      lg: "90px 200px minmax(220px, 320px) 100px",
                    },
                    alignItems: "center",
                    width: "100%",
                    justifyContent: "start",
                  }}
                >
                  <SearchSectionLabel>Search By</SearchSectionLabel>
                  <FormInput
                    type="select"
                    size="small"
                    fullWidth
                    value={searchBy}
                    onChange={changeSearchBy}
                    options={[
                      { value: "modelId", label: "Model Id" },
                      { value: "modelNumber", label: "Model Number" },
                    ]}
                    sx={{
                      minWidth: { xs: "100%", sm: "100%", lg: 200 },
                      maxWidth: { xs: "100%", sm: "100%", lg: 200 },
                      "& .MuiOutlinedInput-root": { height: fieldHeight },
                      ...focusOutlineSx,
                    }}
                    MenuProps={selectMenuProps}
                  />
                  <FormInput
                    type="text"
                    name="text"
                    placeholder={
                      searchBy === "modelId"
                        ? "Enter Model Id"
                        : "Enter Model Number"
                    }
                    value={text}
                    onChange={onChange}
                    size="small"
                    fullWidth
                    margin="none"
                    startAdornment={<SearchIcon fontSize="small" />}
                    sx={{
                      margin: 0,
                      "& .MuiOutlinedInput-root": { height: fieldHeight },
                      ...focusOutlineSx,
                    }}
                  />
                  <AppButton
                    type="submit"
                    variant="primary"
                    size="medium"
                    sx={{
                      width: { xs: "100%", lg: "auto" },
                      justifySelf: { xs: "start", lg: "start" },
                    }}
                  >
                    Search
                  </AppButton>
                </Box>
              </Box>
            </Paper>
          </Grid2>
        </Grid2>
        <Box sx={{ my: 2 }}>
          <Alert />
        </Box>
        {showText && Object.keys(ModelData).length > 0 && (
          <Paper sx={{ p: 2, boxShadow: 2, borderRadius: 1 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 1,
                mb: 2,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SectionHeading sx={{ mb: 0 }}>Device Information</SectionHeading>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  flexWrap: { xs: "wrap", sm: "nowrap" },
                  width: { xs: "100%", sm: "auto" },
                  justifyContent: { xs: "flex-start", sm: "flex-end" },
                }}
              >
                {roles.includes(customConfig.roleProductManager) &&
                  customConfig.allowedDeviceModelCategories.includes(
                    ModelData.category,
                  ) && (
                    <AppButton
                      component={Link}
                      to="/addNewDeviceModel"
                      state={{
                        editDeviceModel: true,
                        deviceModelData: ModelData,
                        freqToPowerData: freqToPowerData,
                        modelId: ModelData?.modelId,
                        modelNumber: ModelData?.modelNumber,
                      }}
                      variant="primary"
                      // size="small"
                      startIcon={<EditIcon fontSize="small" />}
                      sx={{ width: { xs: "calc(50% - 4px)", sm: "auto" } }}
                    >
                      Edit
                    </AppButton>
                  )}
                <AppButton
                  variant="primary"
                  // size="small"
                  startIcon={<DownloadIcon fontSize="small" />}
                  onClick={downloadDeviceMetadata}
                  sx={{
                    width:
                      roles.includes(customConfig.roleProductManager) &&
                      customConfig.allowedDeviceModelCategories.includes(
                        ModelData.category,
                      )
                        ? { xs: "calc(50% - 4px)", sm: "auto" }
                        : { xs: "100%", sm: "auto" },
                  }}
                >
                  Download
                </AppButton>
              </Box>
            </Box>

            <Grid2 container spacing={1} sx={{ mb: 2 }}>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <InfoField label="Model ID" value={ModelData.modelId} compact />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <InfoField
                  label="Model Number"
                  value={ModelData.modelNumber}
                  compact
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <InfoField
                  label="Model Name"
                  value={ModelData.modelName}
                  compact
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <InfoField
                  label="Category"
                  value={ModelData.category}
                  compact
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <InfoField
                  label="Launch Date"
                  value={
                    Number.isInteger(ModelData.launchDate) ? (
                      <Moment
                        format="MMMM Do YYYY, h:mm:ss a"
                        date={ModelData.launchDate * 1000}
                      />
                    ) : (
                      "-"
                    )
                  }
                  compact
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <InfoField
                  label="IsActive"
                  value={ModelData.isActive}
                  compact
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <InfoField
                  label="Manufacturer"
                  value={ModelData.manufacturer}
                  compact
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <InfoField
                  label="Manufacturer Address"
                  value={ModelData.manufacturerAddress}
                  compact
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <InfoField
                  label="Product Manager ID"
                  value={ModelData.productManagerId}
                  compact
                  valueWeight={400}
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <InfoField label="Version" value={ModelData.version} compact />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }}>
                <InfoField label="Help Doc URL" compact valueWeight={400}>
                  <a
                    href={customConfig.supportURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "inherit",
                      fontWeight: 400,
                      textDecoration: "underline",
                    }}
                  >
                    {ModelData.helpDocUrl}
                  </a>
                </InfoField>
              </Grid2>
              {(ModelData.category === customConfig.decviceCategoryAC ||
                ModelData.category === customConfig.deviceCategoryCAC ||
                ModelData.category === customConfig.deviceCategoryIAC) &&
                ModelData.enrolementProfileId && (
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <InfoField
                      label="Enrolment Profile ID"
                      value={ModelData.enrolementProfileId}
                      compact
                    />
                  </Grid2>
                )}
              {ModelData.category === customConfig.decviceCategoryAC && (
                <>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <InfoField label="Variant" value={ModelData.variant} compact />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <InfoField
                      label="Launch Year"
                      value={ModelData.launchYear}
                      compact
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <InfoField
                      label="Scaling Factor"
                      value={ModelData.scalingFactor}
                      compact
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <InfoField
                      label="Energy Computation Mode"
                      value={ModelData.energyComputationMode}
                      compact
                    />
                  </Grid2>
                </>
              )}
              <Grid2 size={{ xs: 12, md: 6 }} sx={{ mt: 2 }}>
                <InfoField
                  label="Star Rating"
                  value={ModelData.starRating != null ? ModelData.starRating : ""}
                  compact
                />
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }} sx={{ mt: 2 }}>
                <InfoField
                  label="Matter Enabled"
                  value={ModelData.isMatterEnabled === true ? "True" : "False"}
                  compact
                />
              </Grid2>
              {(ModelData.category === customConfig.decviceCategoryAC ||
                ModelData.category === customConfig.deviceCategoryCAC) && (
                <Grid2 size={{ xs: 12, md: 6 }} sx={{ mt: 2 }}>
                  <InfoField
                    label="Tonnage"
                    value={ModelData.tonnage}
                    compact
                  />
                </Grid2>
              )}
              <Grid2 size={{ xs: 12, md: 6 }} sx={{ mt: 2 }}>
                <InfoField
                  label="Chip Type"
                  value={ModelData.chipType}
                  compact
                />
              </Grid2>
              {ModelData.subCategory && ModelData.subCategory.length !== 0 && (
                <Grid2 size={{ xs: 12, md: 6 }}>
                  <InfoField
                    label="Sub Category"
                    value={ModelData.subCategory}
                    compact
                  />
                </Grid2>
              )}
              {ModelData.isMatterEnabled === true && (
                <>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <InfoField
                      label="Product ID"
                      value={ModelData.matterPID}
                      compact
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, md: 6 }}>
                    <InfoField
                      label="Vendor ID"
                      value={ModelData.vendorId}
                      compact
                    />
                  </Grid2>
                </>
              )}
            </Grid2>
            {ModelData.controlCapabilities &&
              ModelData.controlCapabilities.length > 0 && (
              <Box sx={{ mt: 3, width: "100%" }}>
                  <SectionHeading>Control Capability</SectionHeading>
                  <DataTable
                    columns={controlCapabilityColumns}
                    data={controlCapabilityRows}
                    rowKey="rowId"
                    minWidth="100%"
                    stickyHeader
                    striped={false}
                    hoverEffect={false}
                    tableContainerSx={{
                      boxShadow: "none",
                      border: "1px solid",
                      borderColor: "divider",
                    borderRadius: 0,
                    width: { xs: "max-content", md: "100%" },
                    minWidth: { xs: 720, md: "100%" },
                    overflowX: { xs: "auto", md: "hidden" },
                    }}
                  sx={{ overflowX: { xs: "auto", md: "hidden" } }}
                  />
                </Box>
              )}

            <Box sx={{ mt: 3, mb: 2, width: "100%" }}>
              <SectionHeading>Status Capability</SectionHeading>
              <DataTable
                columns={statusCapabilityColumns}
                data={statusCapabilityRows}
                rowKey="rowId"
                minWidth="100%"
                stickyHeader
                striped={false}
                hoverEffect={false}
                compactEmpty={statusCapabilityRows.length === 0}
                tableContainerSx={{
                  boxShadow: "none",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0,
                  width: { xs: "max-content", md: "100%" },
                  minWidth: { xs: 720, md: "100%" },
                  overflowX: { xs: "auto", md: "hidden" },
                }}
                sx={{ overflowX: { xs: "auto", md: "hidden" } }}
              />
            </Box>
            {ModelData.configCapabilities && ModelData.configCapabilities.length > 0 && (
            <Box sx={{ mt: 3, mb: 2, width: "100%" }}>
              <SectionHeading>Config Capability</SectionHeading>
              <DataTable
                columns={configCapabilityColumns}
                data={configCapabilityRows}
                rowKey="rowId"
                minWidth="100%"
                stickyHeader
                striped={false}
                hoverEffect={false}
                tableContainerSx={{
                  boxShadow: "none",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0,
                  width: { xs: "max-content", md: "100%" },
                  minWidth: { xs: 720, md: "100%" },
                  overflowX: { xs: "auto", md: "hidden" },
                }}
                sx={{ overflowX: { xs: "auto", md: "hidden" } }}
              />
            </Box>
            )}
            {ModelData.category === customConfig.decviceCategoryAC && (
            <Box sx={{ mt: 3, mb: 2, width: "100%" }}>
              <SectionHeading>Diag Capability</SectionHeading>
              <DataTable
                columns={statusCapabilityColumns}
                data={diagCapabilityRows}
                rowKey="rowId"
                minWidth="100%"
                stickyHeader
                striped={false}
                hoverEffect={false}
                compactEmpty={diagCapabilityRows.length === 0}
                tableContainerSx={{
                  boxShadow: "none",
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 0,
                  width: { xs: "max-content", md: "100%" },
                  minWidth: { xs: 720, md: "100%" },
                  overflowX: { xs: "auto", md: "hidden" },
                }}
                sx={{ overflowX: { xs: "auto", md: "hidden" } }}
              />
            </Box>
            )}
          </Paper>
        )}
      </Box>
    </ResponsiveContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  ModelData: state.getModel,
  roles: state.auth.roles,
  freqToPowerData: state.report.freqToPowerData,
});

export default connect(mapStateToProps, {
  setAlert,
  getModelId,
  getDeviceByModelNumber,
  getFreqToPower,
})(SearchModel);
